/** Sort phone model names newest → oldest for pickers and browse UI. */

const VARIANT_SCORE: [RegExp, number][] = [
  [/pro\s*max|ultra\b/i, 55],
  [/pro\s*\+|pro\s*plus/i, 48],
  [/\bpro\b/i, 42],
  [/\+/i, 32],
  [/\bplus\b/i, 32],
  [/\bmax\b/i, 28],
  [/\bair\b/i, 22],
  [/\bfold\b|\bflip\b|\btri/i, 38],
  [/\bmini\b/i, 12],
  [/\bfe\b|\be\b/i, 8],
  [/\bse\b/i, 15],
]

function generationFromName(name: string): number {
  const n = name.toLowerCase()
  const patterns = [
    /iphone\s*(?:se\s*)?(\d{1,2})/i,
    /galaxy\s*[szafm]\s*(\d{1,2})/i,
    /galaxy\s*(\d{1,2})/i,
    /pixel\s*(\d{1,2})/i,
    /(?:xiaomi|redmi|poco)\s*(\d{1,2})/i,
    /oneplus\s*(\d{1,2})/i,
    /xperia\s*(\d{1,2})/i,
    /find\s*x\s*(\d)/i,
    /reno\s*(\d{1,2})/i,
    /magic\s*(\d)/i,
    /(?:edge|moto\s*g)\s*(\d{1,2})/i,
    /(?:^|\s)(\d{1,2})(?:\s|$)/,
  ]
  for (const re of patterns) {
    const m = n.match(re)
    if (m) return parseInt(m[1], 10)
  }
  const year = n.match(/\((20\d{2})\)/)
  if (year) return Math.max(0, parseInt(year[1], 10) - 2000)
  return 0
}

function variantScore(name: string): number {
  for (const [re, score] of VARIANT_SCORE) {
    if (re.test(name)) return score
  }
  return 20
}

/** Higher = newer / higher tier. */
export function phoneModelNewnessScore(modelName: string): number {
  const gen = generationFromName(modelName)
  return gen * 100 + variantScore(modelName)
}

export function comparePhoneModelsByNewness(a: string, b: string): number {
  const sa = phoneModelNewnessScore(a)
  const sb = phoneModelNewnessScore(b)
  if (sb !== sa) return sb - sa
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
}

const CATALOG_UNKNOWN_RANK = 9_000

/** Sort API phone-model rows newest → oldest (release year, catalog order, name heuristics). */
export function comparePhoneModelRowsNewestFirst<
  T extends { manufacturer: string; model_name: string; release_year?: number | null },
>(
  a: T,
  b: T,
  catalogRank?: (manufacturer: string, modelName: string) => number,
): number {
  const yearA = a.release_year ?? null
  const yearB = b.release_year ?? null
  if (yearA != null && yearB != null && yearA !== yearB) return yearB - yearA
  if (yearA != null && yearB == null) return -1
  if (yearA == null && yearB != null) return 1

  if (catalogRank) {
    const rankA = catalogRank(a.manufacturer, a.model_name)
    const rankB = catalogRank(b.manufacturer, b.model_name)
    const inA = rankA < CATALOG_UNKNOWN_RANK
    const inB = rankB < CATALOG_UNKNOWN_RANK
    if (inA && inB && rankA !== rankB) return rankA - rankB
    if (inA !== inB) return inA ? -1 : 1
  }

  return comparePhoneModelsByNewness(a.model_name, b.model_name)
}

/** @deprecated Use comparePhoneModelRowsNewestFirst */
export function comparePhoneModelRowsOldestFirst<
  T extends { manufacturer: string; model_name: string; release_year?: number | null },
>(
  a: T,
  b: T,
  catalogRank?: (manufacturer: string, modelName: string) => number,
): number {
  return comparePhoneModelRowsNewestFirst(a, b, catalogRank)
}

export function sortPhoneModelNames(models: string[]): string[] {
  return [...models].sort(comparePhoneModelsByNewness)
}

export function sortPhoneModelRows<T extends { model_name: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => comparePhoneModelsByNewness(a.model_name, b.model_name))
}
